import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { syncOfflineChanges } from '../slice/functions';

/**
 * Custom hook to manage offline/online sync notifications
 */
export const useOfflineSync = () => {
    const [offlineNotification, setOfflineNotification] = useState(null);
    const dispatch = useDispatch();

    useEffect(() => {
        const handleOfflineMode = (event) => {
            setOfflineNotification({ type: 'warning', message: event.detail.message });
        };

        const handleSyncSuccess = (event) => {
            setOfflineNotification({ type: 'success', message: event.detail.message });
            // Auto-hide success message after 3 seconds
            setTimeout(() => setOfflineNotification(null), 3000);
        };

        window.addEventListener('app-offline-mode', handleOfflineMode);
        window.addEventListener('app-sync-success', handleSyncSuccess);

        // Check online status on mount
        if (!navigator.onLine) {
            setOfflineNotification({
                type: 'warning',
                message: '⚠️ Working offline - changes will sync when back online'
            });
        }

        // Listen for browser online/offline events
        const handleOnline = async () => {
            setOfflineNotification({ type: 'info', message: '🔄 Back online - syncing changes...' });

            // Trigger sync of offline changes
            try {
                const result = await syncOfflineChanges(dispatch);

                if (result.synced > 0) {
                    setOfflineNotification({
                        type: 'success',
                        message: `✅ Successfully synced ${result.synced} change(s) to cloud`
                    });
                    setTimeout(() => setOfflineNotification(null), 3000);
                } else {
                    setOfflineNotification(null);
                }
            } catch (error) {
                console.error('Sync failed:', error);
                setOfflineNotification({
                    type: 'warning',
                    message: '⚠️ Sync failed - will retry on next change'
                });
                setTimeout(() => setOfflineNotification(null), 3000);
            }
        };

        const handleOffline = () => {
            setOfflineNotification({
                type: 'warning',
                message: '⚠️ Working offline - changes will sync when back online'
            });
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('app-offline-mode', handleOfflineMode);
            window.removeEventListener('app-sync-success', handleSyncSuccess);
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [dispatch]);

    return offlineNotification;
};
